import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import ErrorHandler from '../../utils/errorHandler';
import Logo from '../ui/Logo';
import CmsPageRenderer from './CmsPageRenderer';
import { useCart } from '../../contexts/CartContext';

const PublicCmsPage = ({ slug }) => {
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getCartCount } = useCart();

  const menuItems = useMemo(() => resolved?.menu?.items || [], [resolved]);
  const config = useMemo(() => resolved?.version?.config || {}, [resolved]);
  const renderConfig = useMemo(() => {
    // Landing page should always show pricing; CMS can still override by explicitly
    // including a pricing section (we only inject when missing).
    if (slug !== 'home') return config;

    const sections = Array.isArray(config?.sections) ? config.sections : [];
    const hasPricing = sections.some((s) => String(s?.type || '').trim() === 'pricing');
    if (hasPricing) return config;

    return {
      ...config,
      sections: [...sections, { id: 'pricing-auto', type: 'pricing', props: {} }],
    };
  }, [config, slug]);
  const cartCount = getCartCount();

  const resolveMenuHref = useCallback((item) => {
    const rawHref = String(item?.href || '').trim();
    if (!rawHref) return '';

    // Strip origin if CMS stored a full URL.
    const hrefWithoutOrigin = rawHref.replace(/^https?:\/\/[^/]+/i, '');
    const normalizedPath = hrefWithoutOrigin.replace(/\/+$/, '');
    const normalizedNoLeadingSlash = normalizedPath.replace(/^\//, '');

    // If CMS (or old configs) point Pricing to the pricing page, force landing-page section.
    if (
      normalizedPath === '/pricing' ||
      normalizedNoLeadingSlash === 'pricing' ||
      normalizedPath === '/subscription/pricing' ||
      normalizedNoLeadingSlash === 'subscription/pricing'
    ) {
      return '#pricing';
    }

    // Normalize pricing page hashes to landing page anchors.
    if (
      normalizedPath.startsWith('/subscription/pricing#') ||
      normalizedNoLeadingSlash.startsWith('subscription/pricing#') ||
      normalizedPath.startsWith('/pricing#') ||
      normalizedNoLeadingSlash.startsWith('pricing#')
    ) {
      const hashPart = String(normalizedPath.split('#')[1] || '').trim();
      return `#${hashPart || 'pricing'}`;
    }

    // Anchors should scroll on the landing page.
    if (item?.type === 'anchor') {
      return hrefWithoutOrigin.startsWith('#') ? hrefWithoutOrigin : `#${hrefWithoutOrigin}`;
    }

    // Normalize same-page hashes that were stored as absolute paths.
    if (hrefWithoutOrigin.startsWith('/#')) {
      return hrefWithoutOrigin.slice(1);
    }

    return hrefWithoutOrigin;
  }, []);

  const handleAnchorClick = useCallback((event, href) => {
    if (!href || !href.startsWith('#')) return;
    event.preventDefault();

    const id = href.slice(1);
    const el = id ? document.getElementById(id) : null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Keep URL hash in sync for refresh/share.
    window.history.replaceState(null, '', href);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/public/cms/pages/${encodeURIComponent(slug)}`);
        setResolved(res.data);

        // Best-effort analytics
        api.post(`/public/cms/pages/${encodeURIComponent(slug)}/view`).catch(() => undefined);
      } catch (err) {
        ErrorHandler.logError(err, 'Failed to load CMS page');
      }
      setLoading(false);
    };

    setLoading(true);
    setResolved(null);
    load();
  }, [slug]);

  useEffect(() => {
    if (loading) return () => {};

    const hash = String(window.location.hash || '').trim();
    if (!hash || !hash.startsWith('#')) return () => {};

    const id = hash.slice(1);
    if (!id) return () => {};

    // Wait a tick for CMS sections to render.
    const timer = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);

    return () => window.clearTimeout(timer);
  }, [loading, resolved]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1115] text-white">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"
          aria-label="Loading page"
        />
      </div>
    );
  }

  // If CMS isn't configured/published yet, still render the landing page shell.
  // (Other slugs should still behave like real CMS pages and 404.)
  if (!resolved && slug !== 'home') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1115] text-[#f4efe6]">
        <div className="text-center">
          <div className="text-xl font-semibold mb-2">Page not found</div>
          <Link to="/" className="text-emerald-200 hover:text-emerald-100">
            Go home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0f1115] text-[#f4efe6]">
      <div
        className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(16,185,129,0.12),transparent_32%),radial-gradient(circle_at_82%_10%,rgba(14,165,233,0.12),transparent_34%),radial-gradient(circle_at_50%_82%,rgba(234,179,8,0.08),transparent_32%)]"
        aria-hidden="true"
      />

      <div className="relative">
        <nav className="fixed inset-x-0 top-0 z-30 border-b border-white/10 bg-[#0f1115]/85 backdrop-blur">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
            <Link to="/">
              <Logo size="sm" showTagline={false} />
            </Link>

            <div className="hidden items-center gap-6 text-sm text-[#d8d2c5] md:flex">
              {menuItems.map((item) => {
                const href = resolveMenuHref(item);
                const key = `${item?.type || 'link'}-${href}-${item?.label || ''}`;

                if (href.startsWith('#')) {
                  return (
                    <a
                      key={key}
                      href={href}
                      onClick={(e) => handleAnchorClick(e, href)}
                      className="hover:text-emerald-200"
                    >
                      {item.label}
                    </a>
                  );
                }

                if (item.type === 'internal') {
                  return (
                    <Link key={key} to={href} className="hover:text-emerald-200">
                      {item.label}
                    </Link>
                  );
                }

                return (
                  <a key={key} href={href} className="hover:text-emerald-200">
                    {item.label}
                  </a>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/subscription/cart"
                className="relative inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-[#d8d2c5] hover:text-emerald-200 hover:border-emerald-400/30 transition"
                aria-label="Cart"
              >
                <span className="mr-2 hidden sm:inline">Cart</span>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 9m13-9l2 9m-5-2a1 1 0 11-2 0 1 1 0 012 0zm-8 0a1 1 0 11-2 0 1 1 0 012 0z"
                  />
                </svg>
                <span className="ml-2 inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-emerald-400 px-2 py-0.5 text-xs font-bold text-[#0f1115]">
                  {cartCount}
                </span>
              </Link>
              <Link
                to="/login"
                className="hidden text-sm font-semibold text-[#d8d2c5] hover:text-emerald-200 md:inline-flex"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-4 py-2 text-sm font-semibold text-[#0f1115] shadow-md shadow-emerald-500/25 hover:translate-y-[-1px] transition"
              >
                Get started
              </Link>
            </div>
          </div>
        </nav>

          <CmsPageRenderer config={renderConfig} />
      </div>
    </div>
  );
};

PublicCmsPage.propTypes = {
  slug: PropTypes.string.isRequired,
};

export default PublicCmsPage;
